import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { buildQueryString, exportFile, showError } from "../../../utils/utilities";
import { useFormik } from "formik";
import { get, isEmpty, toLower } from "lodash";
import { useTranslation } from "react-i18next";
import { langDate, langMonth } from "../../../utils/properties";
import { Col, Row, DatePicker, Select } from "antd";
import { DownloadOutlined, ClearOutlined } from "@ant-design/icons";
import Button from "../../../components/functional_components/Button/PFButton";
import { setLoading } from "../../../redux/ducks/Loading";
import ExportTimesheet from "../../../services/api/ExportTimesheet";
import CostCentre from "../../../services/api/CostCentre";
import ProjectType from "../../../services/api/ProjectType";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const ExportCostDetail = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const dateFormat = langMonth[toLower(currentLanguage)];
  const [state, setState] = useState({
    costCentres: [],
    projectTypes: [],
  });

  useEffect(() => {
    getCostCentres();
    getProjectTypes();
  }, [currentLanguage]);

  const getProjectTypes = async () => {
    dispatch(setLoading(true));
    const projectType = await ProjectType.getProjectType(`allPages=true`);
    const optionList = [];
    if (!projectType.error && projectType.data) {
      projectType.data.forEach((item) => {
        optionList.push({ value: item.id, label: t(`timesheet.export.projectType${item.id}`) });
      });
      setState((prevState) => ({ ...prevState, projectTypes: optionList }));
    } else {
      showError(get(projectType, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const getCostCentres = async () => {
    dispatch(setLoading(true));
    const costCenters = await CostCentre.costCentre(`allPages=true`);
    const optionList = [];
    if (!costCenters.error && costCenters.data) {
      costCenters.data.forEach((item) => {
        optionList.push({ value: item.id, label: item.id });
      });
      setState((prevState) => ({ ...prevState, costCentres: optionList }));
    } else {
      showError(get(costCenters, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const formikFilter = useFormik({
    initialValues: {
      month: [],
      costCentre: [],
      projectType: [],
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      dispatch(setLoading(true));
      const obj = {};
      if (!isEmpty(values.month)) {
        obj.startMonth = dayjs(values.month[0], "YYYYMM").format("YYYYMM");
        obj.endMonth = dayjs(values.month[1], "YYYYMM").format("YYYYMM");
      } else {
        obj.startMonth = null;
        obj.endMonth = null;
      }
      if (!isEmpty(values.costCentre)) {
        obj.costCentresId = values.costCentre;
      } else {
        obj.costCentresId = null;
      }
      if (!isEmpty(values.projectType)) {
        obj.projectTypeId = values.projectType;
      } else {
        obj.projectTypeId = null;
      }
      const queryString = buildQueryString({}, obj);
      const exportResponse = await ExportTimesheet.downloadSquaringTimesheet(queryString.string);
      if (!exportResponse.error) {
        const blob = new Blob([exportResponse]);
        exportFile(blob, "Cost Detail.csv");
      } else {
        showError(get(exportResponse, "error.messageCode", "ER000"));
      }
      dispatch(setLoading(false));
    },
  });

  return (
    <div className="filter-container">
      <Row gutter={[48, 32]}>
        <Col lg={8} md={12} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10 mb-10" htmlFor="month">
              {" "}
              {t("timesheet.export.rangeMonth")}
            </label>
          </Row>
          <Row>
            <RangePicker
              className="w-100"
              picker="month"
              id="month"
              name="month"
              format={dateFormat}
              locale={langDate[`locale_${toLower(currentLanguage)}`]}
              value={formikFilter.values.month}
              onChange={(date) => formikFilter.setFieldValue("month", date)}
            />
          </Row>
        </Col>
        <Col lg={7} md={12} xs={24} sm={12}>
          <Row>
            <label className="fw-bold mt-10 mb-10" htmlFor="costCentre">
              {" "}
              {t("timesheet.export.dependentCostCenter")}
            </label>
          </Row>
          <Row>
            <Select
              mode="multiple"
              className="w-100"
              id="costCentre"
              name="costCentre"
              placeholder={t("timesheet.export.dependentCostCenter")}
              onChange={(value) => formikFilter.setFieldValue("costCentre", value)}
              onBlur={formikFilter.handleBlur("costCentre")}
              value={formikFilter.values.costCentre}
              options={state.costCentres}
            />
          </Row>
        </Col>
        <Col lg={9} md={12} xs={24} sm={12}>
          <Row>
            <label className="fw-bold mt-10 mb-10" htmlFor="projectType">
              {" "}
              {t("timesheet.export.projectType")}
            </label>
          </Row>
          <Row>
            <Select
              mode="multiple"
              className="w-100"
              id="projectType"
              name="projectType"
              placeholder={t("timesheet.export.projectType")}
              onChange={(value) => formikFilter.setFieldValue("projectType", value)}
              onBlur={formikFilter.handleBlur("projectType")}
              value={formikFilter.values.projectType}
              options={state.projectTypes}
            />
          </Row>
        </Col>
      </Row>
      <Row className="mt-1em" justify="end">
        <Col>
          <Button
            isDisabled={!formikFilter.dirty}
            type="secondary"
            currentSize="large"
            clickCallback={formikFilter.resetForm}
            currentIcon={<ClearOutlined className="icon-large" />}
          />
        </Col>
        <Col className="left-margin">
          <Button
            currentSize="large"
            clickCallback={formikFilter.submitForm}
            currentIcon={<DownloadOutlined className="icon-large" />}
          />
        </Col>
      </Row>
    </div>
  );
};

export default ExportCostDetail;
